<template>
    <transition :name="$route.meta.transition || 'fade'">
        <div>
            <TickerTape v-if="tradingView" :options="tickerTapeOptions" />

            <div class="container mx-auto">
                <TradingroomHeader />

                <div id="content" class="flex flex-col lg:flex-row lg:divide-x divide-gray-300">
                    <div class="lg:w-3/4 lg:pr-8">
                        <div id="highlights" class="relative">
                            <div class="flex flex-row justify-between mb-4">
                                <strong class="mb-4 block">
                                    <h1>{{ h1_title }}</h1>
                                </strong>

                                <div class="flex flex-row gap-4 items-center">
                                    <button
                                        v-if="
                                            getQueryParameterByName('author') && getQueryParameterByName('author_name') && selectedUsers.includes(getQueryParameterByName('author'))
                                        "
                                        class="bg-blue text-white px-2 py-0.5 rounded-md flex flex-row gap-2"
                                        @click="selectUser(getQueryParameterByName('author'), true)"
                                    >
                                        <p>{{ $t('all_posts_from') }} {{ getQueryParameterByName('author_name') }}</p>
                                        <div class="bg-white text-blue rounded-r-md px-1 -my-0.5 -mr-2 border-2 border-blue">x</div>
                                    </button>
                                    <div v-click-outside="() => (filterOpened = false)" class="z-50">
                                        <div class="flex flex-row gap-2 cursor-pointer p-2 items-center" @click="filterOpened = !filterOpened">
                                            <Icon type="Filter" class="fill-current" viewBox="0 0 21 20" width="21" height="20" />
                                            <p>{{ $t('plain_filter') }}</p>
                                        </div>
                                        <div class="relative">
                                            <TransitionExpand v-show="filterOpened" class="absolute top-0 right-0 z-50 w-full lg:w-[900px]">
                                                <div class="mt-2 rounded-md shadow-md p-4 bg-white flex flex-col gap-4">
                                                    <Select
                                                        v-model:selected="sortBy"
                                                        name="sort_by"
                                                        :no-value-allowed="true"
                                                        :form-component-id="$.uid"
                                                        :label="$t('sort_by')"
                                                        :options="sortByOptions"
                                                        :multiple="false"
                                                        class="w-full lg:w-1/3 self-end z-30 lg:pl-2"
                                                    >
                                                    </Select>
                                                    <div class="flex flex-col lg:flex-row gap-4 lg:h-[320px]">
                                                        <div v-if="filters.users?.data" class="w-full max-h-[286px] lg:max-h-full overflow-y-auto">
                                                            <p>{{ $t('filter_by_authors') }}</p>
                                                            <div v-for="user in filters.users.data" :key="user.id">
                                                                <Checkbox
                                                                    :name="$translate(user.first_name) + ' ' + $translate(user.last_name)"
                                                                    :label="$translate(user.first_name) + ' ' + $translate(user.last_name)"
                                                                    :value="selectedUsers.includes(user.id)"
                                                                    class="my-4"
                                                                    @update:value="selectUser(user.id)"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div v-if="filters.postCategories?.data" class="w-full max-h-[286px] lg:max-h-full overflow-y-auto">
                                                            <p>{{ $t('filter_by_category') }}</p>
                                                            <div v-for="category in filters.postCategories.data" :key="category.id">
                                                                <Checkbox
                                                                    :name="$translate(category.name)"
                                                                    :label="$translate(category.name)"
                                                                    :value="selectedCategories.includes(category.name)"
                                                                    class="my-4"
                                                                    @update:value="selectCategory(category.name)"
                                                                />
                                                            </div>
                                                        </div>
                                                        <Datepicker
                                                            :format="calendarModeltype"
                                                            class="!m-0"
                                                            :class="filters.users?.data ? 'w-full' : 'w-1/2 lg:w-full'"
                                                            :model-value="date"
                                                            :locale="calendarLocale"
                                                            :select-text="$t('plain_select')"
                                                            :enable-time-picker="false"
                                                            :range="true"
                                                            inline
                                                            auto-apply
                                                            :monday-first="true"
                                                            @update:model-value="setDate"
                                                        />
                                                    </div>

                                                    <hr class="border-gray-500 w-full my-4" />

                                                    <div class="flex flex-row gap-2 justify-between">
                                                        <p class="text-blue cursor-pointer" @click="reset()">
                                                            {{ $t('plain_reset') }}
                                                        </p>
                                                        <p class="text-blue cursor-pointer" @click="search()">
                                                            {{ $t('plain_apply') }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </TransitionExpand>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Loading :loading="loading">
                                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <TradingRoomPostItem
                                        v-for="item in $store.getters['posts/getByKey']('index').data"
                                        :key="item.id"
                                        layout="no_image"
                                        class="bg-white shadow-md rounded-md"
                                        :contentstream="item"
                                    />
                                </div>
                            </Loading>

                            <div class="flex flex-row justify-between mt-4">
                                <router-link :to="link" class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2 cursor-pointer">
                                    <span>
                                        {{ $t('tradingroom') }}
                                    </span>
                                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                                </router-link>
                                <Pagination :limit="3" :data="$store.getters['posts/getByKey']('index')" @pagination-change-page="paginate" />
                            </div>
                        </div>
                    </div>
                    <div class="lg:w-1/4 lg:pl-8">
                        <SidebarList />
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import _ from 'lodash';
import { TickerTape } from 'vue-tradingview-widgets';
import { mapGetters } from 'vuex';
import api from '../api';
import { useCcm19Global } from '../useCcm19Global';

export default {
    components: {
        TickerTape
    },
    setup() {
        const { onReady, tradingView } = useCcm19Global();
        onReady(() => {});

        return { tradingView };
    },

    mounted() {
        this.loading = true;
        const promisses = [];

        if (this.getQueryParameterByName('author')) {
            this.selectUser(this.getQueryParameterByName('author'));
        }

        promisses.push(
            this.indexContentstreams().then(() => {
                this.loading = false;
            })
        );

        promisses.push(
            api.get('/api/v1/posts/filters').then((response) => {
                this.filters = response.data;
            })
        );

        Promise.all(promisses).then(() => {
            this.loading = false;
        });
    },

    methods: {
        paginate(page) {
            this.indexContentstreams({
                page
            });
        },

        reset() {
            this.filterOpened = false;
            this.date = null;
            this.selectedUsers = [];
            this.selectedTags = [];
            this.selectedCategories = [];
            this.indexContentstreams();
        },

        setDate(date) {
            this.date = [new Date(this.$moment(date[0]).startOf('day')), new Date(this.$moment(date[1]).endOf('day'))];
        },

        search() {
            this.indexContentstreams();
        },

        selectUser(user_id, reloadSearch = false) {
            if (this.selectedUsers.includes(user_id)) {
                this.selectedUsers.splice(this.selectedUsers.indexOf(user_id), 1);
                this.removeQueryParameterByName('author', user_id);
            } else {
                this.selectedUsers.push(user_id);
            }

            if (reloadSearch === true) {
                this.search();
            }
        },

        selectTag(tag_id) {
            if (this.selectedTags.includes(tag_id)) {
                this.selectedTags.splice(this.selectedTags.indexOf(tag_id), 1);
            } else {
                this.selectedTags.push(tag_id);
            }
        },

        selectCategory(category_id) {
            if (this.selectedCategories.includes(category_id)) {
                this.selectedCategories.splice(this.selectedCategories.indexOf(category_id), 1);
            } else {
                this.selectedCategories.push(category_id);
            }
        },

        async indexContentstreams(params) {
            this.loading = true;
            const config = {
                params: {
                    user_id: this.selectedUsers,
                    post_tags: this.selectedTags,
                    post_categories: this.selectedCategories,
                    published_at: this.date,
                    key: 'index',
                    'sort[by]': 'created_at',
                    'sort[order]': this.sortBy
                }
            };

            _.merge(config.params, params);

            return await this.$store.dispatch('posts/index', config).then(() => {
                this.loading = false;
                this.activeTopPost = _.first(this.$store.getters['posts/getByKey']('index').data).id;
            });
        }
    },

    computed: {
        link() {
            return this.getApplicationType() === 'backend' ? '/dashboard/hkcmnews/' : '/hkcmnews/';
        },

        ...mapGetters('settings', {
            getApplicationType: 'getApplicationType'
        }),
        region() {
            return window.meta.region_configuration.region || 'de';
        },
        calendarLocale() {
            return this.region === 'de' ? 'de-DE' : 'en-GB';
        },
        calendarModeltype() {
            return this.region === 'de' ? 'dd.mm.yyyy' : 'yyyy-mm-dd';
        }
    },

    // Switch for meta data
    beforeRouteEnter(to, from, next) {
        const title_Aktien = 'Aktien-News: Aktuelle Nachrichten zu Aktien | HKCM';
        const title_Kryptowaehrung = 'Krypto-News: Aktuelle Nachrichten zu Kryptowährungen | HKCM';
        const title_Rohstoffe = 'Rohstoffe-News: Aktuelle Nachrichten zu Rohstoffen | HKCM';
        const title_DAX40 = 'DAX-News: Aktuelle Nachrichten zum DAX40 | HKCM';
        const title_Tech33 = 'Aktuelle Nachrichten zu Technologie-Aktien | HKCM';
        const title_Cannabis = 'Aktuelle Nachrichten zu Cannabis-Aktien | HKCM';
        const title_DOW30 = 'DOW-Jones-News: Aktuelle Nachrichten zum DOW30 | HKCM';
        const title_SP500 = 'S&P500-News: Aktuelle Nachrichten zum S&P500 | HKCM';
        const title_Nasdaq = 'Nasdaq-News: Aktuelle Nachrichten zur Nasdaq | HKCM';
        const title_Silber = 'Silber-News: Aktuelle Nachrichten zu Silber | HKCM';

        const description_Aktien =
            'Aktien: Tauchen Sie ein in die Welt der Börsen mit unseren Aktien-News! - Profitieren Sie von fundierten Informationen & Handelstipps - Jetzt informieren!';
        const description_Kryptowaehrung =
            'Kryptowährung: erfahren Sie, wie sich die Krypto-Märkte entwickeln - Aktuelle Trends und Analysen für erfolgreiche Investments - Jetzt informieren!';
        const description_Rohstoffe =
            'Rohstoffe: Entdecken Sie die Vielfalt der Rohstoffmärkte - Aktuelle Nachrichten und Experteneinsichten für kluge Anlagestrategien - Jetzt informieren!';
        const description_DAX40 =
            'DAX 40: Alles Wissenswerte zum DAX40-Index - Bleiben Sie informiert über deutsche Top-Unternehmen und ihre Aktienentwicklung - Jetzt informieren!';
        const description_Tech33 =
            'Tech 33: Die Zukunft der Technologie-Aktien - Verpassen Sie keine Neuigkeiten aus der innovativen Welt der Technologieunternehmen - Jetzt informieren!';
        const description_Cannabis = 'Cannabis: High Potentials in der Cannabis-Branche - Erfahren Sie alles über wachsende Trends und Investmentchancen - Jetzt informieren!';
        const description_DOW30 = 'DOW 30: Aktuelle News zum DOW30-Index - Begleiten Sie die Entwicklung der 30 bedeutendsten US-Unternehmen an der Börse - Jetzt informieren!';
        const description_SP500 = 'S&P500: Immer einen Schritt voraus mit den S&P500-News - Verfolgen Sie die Kursentwicklung der Top-US-Unternehmen - Jetzt informieren!';
        const description_Nasdaq = 'Nasdaq: Von Technologie bis Biotech - Bleiben Sie up-to-date mit den neuesten Entwicklungen an der Nasdaq - Jetzt informieren!';
        const description_Silber = 'Silber: Glänzende Aussichten für Silber-Investoren - Verpassen Sie keine Chance in diesem spannenden Marktsegment - Jetzt informieren!';

        const path_Aktien = '/hkcmnews/aktien-news-und-analysen';
        const path_Kryptowaehrung = '/hkcmnews/krypto-news-und-analysen';
        const path_Rohstoffe = '/hkcmnews/rohstoffe-news-und-analysen';
        const path_DAX40 = '/hkcmnews/dax-news-und-analysen';
        const path_Tech33 = '/hkcmnews/tech-aktien-news-und-analysen';
        const path_Cannabis = '/hkcmnews/cannabis-aktien-news-und-analysen';
        const path_DOW30 = '/hkcmnews/dow-jones-news-und-analysen';
        const path_SP500 = '/hkcmnews/sp500-news-und-analysen';
        const path_Nasdaq = '/hkcmnews/nasdaq-news-und-analysen';
        const path_Silber = '/hkcmnews/silber-news-und-analysen';

        // Set the meta description based on the route's meta information
        if (to.fullPath == path_Aktien) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Aktien);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Aktien);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Aktien);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Aktien);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Aktien);
        } else if (to.fullPath == path_Kryptowaehrung) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Kryptowaehrung);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Kryptowaehrung);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Kryptowaehrung);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Kryptowaehrung);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Kryptowaehrung);
        } else if (to.fullPath == path_Rohstoffe) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Rohstoffe);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Rohstoffe);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Rohstoffe);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Rohstoffe);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Rohstoffe);
        } else if (to.fullPath == path_DAX40) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_DAX40);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_DAX40);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_DAX40);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_DAX40);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_DAX40);
        } else if (to.fullPath == path_Tech33) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Tech33);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Tech33);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Tech33);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Tech33);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Tech33);
        } else if (to.fullPath == path_Cannabis) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Cannabis);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Cannabis);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Cannabis);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Cannabis);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Cannabis);
        } else if (to.fullPath == path_DOW30) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_DOW30);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_DOW30);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_DOW30);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_DOW30);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_DOW30);
        } else if (to.fullPath == path_SP500) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_SP500);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_SP500);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_SP500);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_SP500);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_SP500);
        } else if (to.fullPath == path_Nasdaq) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Nasdaq);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Nasdaq);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Nasdaq);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Nasdaq);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Nasdaq);
        } else if (to.fullPath == path_Silber) {
            document.querySelector('meta[itemprop="description"]').setAttribute('content', description_Silber);
            document.querySelector('meta[name="twitter:title"]').setAttribute('content', title_Silber);
            document.querySelector('meta[name="twitter:description"]').setAttribute('content', description_Silber);
            document.querySelector('meta[name="og:title"]').setAttribute('content', title_Silber);
            document.querySelector('meta[name="og:description"]').setAttribute('content', description_Silber);
        }

        next();
    },

    data: () => ({
        tickerTapeOptions: {
            symbols: [
                {
                    proName: 'FOREXCOM:SPXUSD',
                    title: 'S&P 500'
                },
                {
                    proName: 'BITSTAMP:BTCUSD',
                    title: 'Bitcoin'
                },
                {
                    proName: 'BITSTAMP:ETHUSD',
                    title: 'Ethereum'
                },
                {
                    description: 'Nasdaq 100',
                    proName: 'CME_MINI:NQ1!'
                },
                {
                    description: 'DJIA',
                    proName: 'CBOT_MINI:YM1!'
                },
                {
                    description: 'DAX',
                    proName: 'XETR:DAX'
                },
                {
                    description: 'Gold',
                    proName: 'COMEX_MINI:QO1!'
                },
                {
                    description: 'Silber',
                    proName: 'COMEX:SI1!'
                },
                {
                    description: 'WTI',
                    proName: 'NYMEX:CL1!'
                },
                {
                    description: 'Erdgas',
                    proName: 'CURRENCYCOM:NATURALGAS'
                },
                {
                    description: 'EUR/USD',
                    proName: 'FX:EURUSD'
                }
            ],
            showSymbolLogo: true,
            colorTheme: 'light',
            isTransparent: false,
            displayMode: 'adaptive',
            locale: 'de'
        },
        loading: true,
        activeTopPost: 0,
        filterOpened: false,
        filters: [],
        selectedUsers: [],
        selectedTags: [],
        selectedCategories: [],
        date: null,
        sortBy: 'desc',
        sortByOptions: [
            {
                value: 'asc',
                label: 'älteste zuerst'
            },
            {
                value: 'desc',
                label: 'neuste zuerst'
            }
        ],

        description_Aktien:
            'Aktien: Tauchen Sie ein in die Welt der Börsen mit unseren Aktien-News! - Profitieren Sie von fundierten Informationen & Handelstipps - Jetzt informieren!',
        description_Kryptowaehrung:
            'Kryptowährung: erfahren Sie, wie sich die Krypto-Märkte entwickeln - Aktuelle Trends und Analysen für erfolgreiche Investments - Jetzt informieren!',
        description_Rohstoffe:
            'Rohstoffe: Entdecken Sie die Vielfalt der Rohstoffmärkte - Aktuelle Nachrichten und Experteneinsichten für kluge Anlagestrategien - Jetzt informieren!',
        description_DAX40: 'DAX 40: Alles Wissenswerte zum DAX40-Index - Bleiben Sie informiert über deutsche Top-Unternehmen und ihre Aktienentwicklung - Jetzt informieren!',
        description_Tech33:
            'Tech 33: Die Zukunft der Technologie-Aktien - Verpassen Sie keine Neuigkeiten aus der innovativen Welt der Technologieunternehmen - Jetzt informieren!',
        description_Cannabis: 'Cannabis: High Potentials in der Cannabis-Branche - Erfahren Sie alles über wachsende Trends und Investmentchancen - Jetzt informieren!',
        description_DOW30: 'DOW 30: Aktuelle News zum DOW30-Index - Begleiten Sie die Entwicklung der 30 bedeutendsten US-Unternehmen an der Börse - Jetzt informieren!',
        description_SP500: 'S&P500: Immer einen Schritt voraus mit den S&P500-News - Verfolgen Sie die Kursentwicklung der Top-US-Unternehmen - Jetzt informieren!',
        description_Nasdaq: 'Nasdaq: Von Technologie bis Biotech - Bleiben Sie up-to-date mit den neuesten Entwicklungen an der Nasdaq - Jetzt informieren!',
        description_Silber: 'Silber: Glänzende Aussichten für Silber-Investoren - Verpassen Sie keine Chance in diesem spannenden Marktsegment - Jetzt informieren!',

        path_Aktien: '/hkcmnews/aktien-news-und-analysen',
        path_Kryptowaehrung: '/hkcmnews/krypto-news-und-analysen',
        path_Rohstoffe: '/hkcmnews/rohstoffe-news-und-analysen',
        path_DAX40: '/hkcmnews/dax-news-und-analysen',
        path_Tech33: '/hkcmnews/tech-aktien-news-und-analysen',
        path_Cannabis: '/hkcmnews/cannabis-aktien-news-und-analysen',
        path_DOW30: '/hkcmnews/dow-jones-news-und-analysen',
        path_SP500: '/hkcmnews/sp500-news-und-analysen',
        path_Nasdaq: '/hkcmnews/nasdaq-news-und-analysen',
        path_Silber: '/hkcmnews/silber-news-und-analysen'
    }),

    created() {
        // Switch Filter for categories
        const fullPath = this.$route.fullPath;

        if (fullPath == this.path_Aktien) {
            this.h1_title = 'Aktien-News & Analysen: Aktuelle Nachrichten zu Aktien';
            this.selectedCategories = ['Aktienindizes', 'Indizes', 'IPO'];
        } else if (fullPath == this.path_Kryptowaehrung) {
            this.h1_title = 'Krypto-News & Analysen: Aktuelle Nachrichten zu Kryptowährungen';
            this.selectedCategories = ['Kryptowährung'];
        } else if (fullPath == this.path_Rohstoffe) {
            this.h1_title = 'Rohstoffe-News & Analysen: Aktuelle Nachrichten zu Rohstoffen';
            this.selectedCategories = ['Rohstoffe'];
        } else if (fullPath == this.path_DAX40) {
            this.h1_title = 'DAX-News & Analysen: Aktuelle Nachrichten zum DAX40';
            this.selectedCategories = ['DAX40'];
        } else if (fullPath == this.path_Tech33) {
            this.h1_title = 'Tech-Aktien-News & Analysen';
            this.selectedCategories = ['TECH33'];
        } else if (fullPath == this.path_Cannabis) {
            this.h1_title = 'Cannabis-Aktien-News & Analysen';
            this.selectedCategories = ['Cannabis'];
        } else if (fullPath == this.path_DOW30) {
            this.h1_title = 'DOW-Jones-News & Analysen: Aktuelle Nachrichten zum DOW30';
            this.selectedCategories = ['DOW30'];
        } else if (fullPath == this.path_SP500) {
            this.h1_title = 'S&P500-News & Analysen: Aktuelle Nachrichten zum S&P500';
            this.selectedCategories = ['S&P500'];
        } else if (fullPath == this.path_Nasdaq) {
            this.h1_title = 'Nasdaq-News & Analysen: Aktuelle Nachrichten zur Nasdaq';
            this.selectedCategories = ['Nasdaq'];
        } else if (fullPath == this.path_Silber) {
            this.h1_title = 'Silber-News & Analysen: Aktuelle Nachrichten zu Silber';
            this.selectedCategories = ['Silber'];
        }
    }
};
</script>
